import { format } from 'date-fns'
import { User, Clock, Calendar, Compass, Lightbulb, ScrollText } from 'lucide-react'
import { Button } from 'antd'
import { useNavigate } from 'react-router-dom'
import type { InterviewNotesItem, InterviewQuestion } from '@bandos/shared'
import {
  Card,
  CardHeader,
  InterviewMeta,
  InterviewTitle,
  MetaRow,
  MetaItem,
  QuoteBlock,
  QuoteMark,
  QuoteText,
  QuoteAttribution,
  SectionsGrid,
  Section,
  SectionTitle,
  UnmetNeedsTitle,
  InsightsTitle,
  FactList,
  FactLine,
  ItemList,
  ListItem,
  PendingState,
  LegacySummarySection,
  LegacySummaryLabel,
  LegacySummaryText,
} from './InterviewSummaryCardStyles'

export interface InterviewSummaryCardProps {
  note: InterviewNotesItem
  index: number
  questions: InterviewQuestion[]
  nodeId: string
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

export function InterviewSummaryCard({
  note,
  index,
  questions: _questions,
  nodeId,
}: InterviewSummaryCardProps) {
  const navigate = useNavigate()
  const name = note.intervieweeName?.trim() || 'Anonymous'
  const date = format(new Date(note.savedAt), 'MMM d, yyyy')
  const structured = note.aiSummaryStructured
  const isPending = !structured && !note.aiSummary

  return (
    <Card>
      <CardHeader>
        <InterviewMeta>
          <InterviewTitle>Interview {index + 1}</InterviewTitle>
          <MetaRow>
            <MetaItem>
              <User size={12} />
              {name}
            </MetaItem>
            <MetaItem>
              <Calendar size={12} />
              {date}
            </MetaItem>
            {note.durationSeconds != null && note.durationSeconds > 0 && (
              <MetaItem>
                <Clock size={12} />
                {formatDuration(note.durationSeconds)}
              </MetaItem>
            )}
          </MetaRow>
        </InterviewMeta>
        <Button
          type="primary"
          icon={<ScrollText size={14} />}
          onClick={() => void navigate(`/interview/${nodeId}/transcript/${note.id}`)}
        >
          View transcript
        </Button>
      </CardHeader>

      {isPending && <PendingState>Generating summary…</PendingState>}

      {structured && (
        <>
          {structured.quote && (
            <QuoteBlock>
              <QuoteMark>"</QuoteMark>
              <QuoteText>{structured.quote}</QuoteText>
              <QuoteAttribution>— {name}</QuoteAttribution>
            </QuoteBlock>
          )}

          <SectionsGrid>
            {structured.quickFacts.length > 0 && (
              <Section>
                <SectionTitle>About this person</SectionTitle>
                <FactList>
                  {structured.quickFacts.map((fact, i) => (
                    <FactLine key={i}>{fact}</FactLine>
                  ))}
                </FactList>
              </Section>
            )}

            {structured.insights.length > 0 && (
              <Section>
                <InsightsTitle>
                  <Lightbulb size={13} />
                  Insights
                </InsightsTitle>
                <ItemList>
                  {structured.insights.map((item, i) => (
                    <ListItem key={i}>{item}</ListItem>
                  ))}
                </ItemList>
              </Section>
            )}

            {structured.opportunities.length > 0 && (
              <Section>
                <UnmetNeedsTitle>
                  <Compass size={13} />
                  Unmet needs
                </UnmetNeedsTitle>
                <ItemList>
                  {structured.opportunities.map((item, i) => (
                    <ListItem key={i}>{item}</ListItem>
                  ))}
                </ItemList>
              </Section>
            )}
          </SectionsGrid>
        </>
      )}

      {!structured && note.aiSummary && (
        <LegacySummarySection>
          <LegacySummaryLabel>Summary</LegacySummaryLabel>
          <LegacySummaryText>{note.aiSummary}</LegacySummaryText>
        </LegacySummarySection>
      )}
    </Card>
  )
}
